import type { ReactNode } from 'react';
import type { PositionStatus } from '@nest-recruit/shared';
import { POSITION_STATUS_TONES } from '../../../components/status-tones';
import { toneClass, type Tone } from '../../../components/Tone';
import { formatNumber } from '../../../lib/format';
import type { RollupCounts } from '../api/organisation-api';
import styles from './SeatMix.module.css';

type SeatCountKey = Exclude<keyof RollupCounts, 'total'>;

export interface SeatSegment {
  readonly key: SeatCountKey;
  readonly status: PositionStatus;
  readonly label: string;
  readonly tone: Tone;
}

/** The five seat states in the order the bar, the legend and the columns all read them. */
export const SEAT_SEGMENTS: readonly SeatSegment[] = [
  { key: 'filled', status: 'filled', label: 'Filled', tone: POSITION_STATUS_TONES.filled },
  { key: 'inRecruitment', status: 'in_recruitment', label: 'Recruiting', tone: POSITION_STATUS_TONES.in_recruitment },
  { key: 'vacant', status: 'vacant', label: 'Vacant', tone: POSITION_STATUS_TONES.vacant },
  { key: 'planned', status: 'planned', label: 'Planned', tone: POSITION_STATUS_TONES.planned },
  { key: 'frozen', status: 'frozen', label: 'Frozen', tone: POSITION_STATUS_TONES.frozen },
];

interface SeatMixProps {
  readonly counts: RollupCounts;
  readonly size?: 'sm' | 'lg';
}

/**
 * One bar split by seat state, so a unit's shape reads at a glance without scanning
 * five columns. Decorative: the numbers sit beside it in words or columns.
 */
export function SeatMix({ counts, size = 'sm' }: SeatMixProps): ReactNode {
  const title = SEAT_SEGMENTS.map((segment) => `${formatNumber(counts[segment.key])} ${segment.label.toLowerCase()}`).join(', ');
  return (
    <span className={`${styles.bar} ${styles[size]}`} title={counts.total > 0 ? title : 'No seats yet'} aria-hidden>
      {SEAT_SEGMENTS.filter((segment) => counts[segment.key] > 0).map((segment) => (
        <span
          key={segment.key}
          className={`${toneClass} ${styles.segment}`}
          data-tone={segment.tone}
          // A segment's width is its share of the seats — measured geometry, the one
          // thing an inline style is for (S2 convention).
          style={{ flexGrow: counts[segment.key] }}
        />
      ))}
    </span>
  );
}

/** The colour swatch a legend or a pill puts beside a seat state's name. */
export function SeatSwatch({ tone }: { readonly tone: Tone }): ReactNode {
  return <span className={`${toneClass} ${styles.swatch}`} data-tone={tone} aria-hidden />;
}
